import { useTranslation } from 'react-i18next'

import {
  type RouteApiWithColumns,
  useColumnsWithOptionalPagination
} from './useColumns'
import { useScreenResolution } from './useScreenResolution'
import Label from '../components/Label'
import LinkText from '../components/LinkText/LinkText'
import type { Column } from '../components/Table/Table'
import { TABLE_CELL_WIDTH } from '../components/Table/table-cell-width'
import Tooltip from '../components/Tooltip/Tooltip'
import styles from '../components/common/GradesTable.module.scss'
import NotApplicable from '../components/common/NotApplicable/NotApplicable'
import {
  type StudentGrade,
  type Grade,
  type ColumnGrade
} from '../types/grades'

const GRADE_CELL_SIZE = 128

type GradeColumnType = 'behaviour' | 'proposed-grade' | 'final-grade' | 'custom'

type GradeCellProps = {
  grade: Grade
  cellId: string
  columnType: GradeColumnType
}

type UseGradesColumnsProps = {
  gradeColumns: ColumnGrade[]
  gradeCell: (props: GradeCellProps) => React.ReactNode
  customGradeColumnHeader: (grade: ColumnGrade) => React.ReactNode
  routeApi: RouteApiWithColumns
}

const useGradesColumns = (props: UseGradesColumnsProps) => {
  const { t } = useTranslation(['common'])
  const { isMobile } = useScreenResolution()

  const allColumns: Column<StudentGrade>[] = [
    {
      dataIndex: ['student', 'fullName'],
      key: 'student',
      label: t('header.student'),
      fixed: isMobile ? false : 'left',
      width: TABLE_CELL_WIDTH.XL,
      className: styles.cellToHighlight,
      render: (value: string, record) => (
        <LinkText
          className={styles.studentName}
          dataTestId="student-name"
          to="/students-and-classes/students/$studentId/details"
          params={{
            studentId: record.student.id
          }}
        >
          {value}
        </LinkText>
      )
    },
    ...(props.gradeColumns.map(gradeColumn => ({
      key: gradeColumn.id,
      render: (record: StudentGrade) => {
        const gradeItem = record.gradeColumns[gradeColumn.id]

        return gradeItem ? (
          <div className={styles.gradeCell}>
            <Label
              id={gradeItem.id}
              label={t('label.select-grade', {
                GRADE: gradeColumn.name,
                STUDENT: record.student.fullName
              })}
              hidden
            />
            {props.gradeCell({
              grade: gradeItem,
              columnType: 'custom',
              cellId: getGradeCellId(gradeColumn.name, record.student.fullName)
            })}
          </div>
        ) : (
          <Tooltip
            renderTriggerAsChild
            trigger={
              <div tabIndex={0} role="button" className={styles.noneGrade}>
                <NotApplicable />
              </div>
            }
            text={t('help.not-assignment-assigned')}
          />
        )
      },
      title: props.customGradeColumnHeader(gradeColumn),
      label: gradeColumn.name,
      width: GRADE_CELL_SIZE,
      filled: true
    })) || []),
    {
      dataIndex: 'attendance',
      render: (value: number) => (
        <span className={styles.attendanceCell}>
          {`${Math.round(value * 100)}%`}
        </span>
      ),
      key: 'attendance',
      className: styles.gradeCell,
      title: (
        <FixedColumnHeader
          className={styles.attendanceHeaderCell}
          tooltip={t('help.attendance-description')}
        >
          {t('header.attendance')}
        </FixedColumnHeader>
      ),
      label: t('header.attendance'),
      width: GRADE_CELL_SIZE,
      isHighlighted: true
    },
    {
      key: 'behaviour',
      title: (
        <FixedColumnHeader
          className={styles.headerCell}
          tooltip={t('help.behaviour-description')}
        >
          {t('header.behaviour')}
        </FixedColumnHeader>
      ),
      render: (record: StudentGrade) => (
        <div className={styles.gradeCell}>
          <Label
            id={record.behaviour.id}
            label={t('label.select-grade', {
              GRADE: t('header.behaviour'),
              STUDENT: record.student.fullName
            })}
            hidden
          />
          {props.gradeCell({
            grade: record.behaviour,
            columnType: 'behaviour',
            cellId: getGradeCellId(
              t('header.behaviour'),
              record.student.fullName
            )
          })}
        </div>
      ),
      label: t('header.behaviour'),
      width: GRADE_CELL_SIZE,
      isHighlighted: true,
      filled: true
    },
    {
      key: 'proposed-grade',
      title: (
        <FixedColumnHeader
          className={styles.headerCell}
          tooltip={t('help.proposed-grade-description')}
        >
          {t('header.proposed-grade')}
        </FixedColumnHeader>
      ),
      label: t('header.proposed-grade'),
      render: (record: StudentGrade) => (
        <div className={styles.gradeCell}>
          <Label
            id={record.proposedGrade.id}
            label={t('label.select-grade', {
              GRADE: t('header.proposed-grade'),
              STUDENT: record.student.fullName
            })}
            hidden
          />
          {props.gradeCell({
            grade: record.proposedGrade,
            columnType: 'proposed-grade',
            cellId: getGradeCellId(
              t('header.proposed-grade'),
              record.student.fullName
            )
          })}
        </div>
      ),
      width: GRADE_CELL_SIZE,
      isHighlighted: true,
      filled: true
    },
    {
      key: 'final-grade',
      title: (
        <FixedColumnHeader
          className={styles.headerCell}
          tooltip={t('help.final-grade-description')}
        >
          {t('header.final-grade')}
        </FixedColumnHeader>
      ),
      label: t('header.final-grade'),
      render: (record: StudentGrade) => (
        <div className={styles.gradeCell}>
          <Label
            id={record.finalGrade.id}
            label={t('label.select-grade', {
              GRADE: t('header.final-grade'),
              STUDENT: record.student.fullName
            })}
            hidden
          />
          {props.gradeCell({
            grade: record.finalGrade,
            columnType: 'final-grade',
            cellId: getGradeCellId(
              t('header.final-grade'),
              record.student.fullName
            )
          })}
        </div>
      ),
      width: GRADE_CELL_SIZE,
      isHighlighted: true,
      filled: true
    }
  ]

  const columns = useColumnsWithOptionalPagination(allColumns, {
    numberColumnClassName: styles.cellToHighlight,
    routeApi: props.routeApi
  })

  return columns
}

const getGradeCellId = (gradeName: string, studentName: string) =>
  `${gradeName}-for-${studentName}`.toLowerCase().replaceAll(' ', '-')

type FixedColumnHeaderProps = {
  tooltip: string
  children: React.ReactNode
  className?: string
}

const FixedColumnHeader = (props: FixedColumnHeaderProps) => (
  <Tooltip
    renderTriggerAsChild
    trigger={<div className={props.className}>{props.children}</div>}
    text={props.tooltip}
  />
)

export default useGradesColumns
